import { useEffect, useState } from 'react'

export interface HeatmapAttraction {
  name: string
  type: string
  hours: Record<number, number>
}

export interface VelocityPoint {
  hour: number
  revenue: number
  orders: number
  cumulative: number
}

export interface PlanScatterPoint {
  plan_name: string
  plan_type: string
  price: number
  units_sold: number
  avg_scans: number
  avg_attractions: number
  avg_duration_min: number
}

export interface StaffSparkline {
  name: string
  total_scans: number
  hours: { hour: number; scans: number; bands: number }[]
}

export interface Insight {
  type: 'highlight' | 'warning' | 'metric'
  icon: string
  title: string
  body: string
}

export interface AttractionDeep {
  attraction_id: number
  name: string
  type: string
  total_scans: number
  peak_scans: number
  peak_hour: number
  avg_scans_per_hour: number
  active_hours: number
  capacity_utilization: { hour: number; scans: number; utilization: number }[]
  cycle_time_sec: number | null
  est_wait_avg_sec: number | null
  est_wait_worst_sec: number | null
  batch_size: number | null
  cycles_per_hour: number | null
  peak_15min_scans: number | null
  throughput_per_min: number | null
  avg_inter_scan_sec: number | null
  avg_scans_per_guest: number | null
  repeat_guest_count: number
  staff_count: number
  staff_scans: number
}

export interface AgentDeep {
  agent_id: number
  name: string
  attractions: string[]
  total_scans: number
  revenue: number
  bands_served: number
  shift_start: number
  shift_end: number
  shift_hours: number
  active_hours: number
  idle_hours: number
  scans_per_hour: number
  utilization_pct: number
  hourly: { hour: number; scans: number }[]
}

export interface TransitNode {
  id: number
  name: string
  traffic: number
  x: number
  y: number
  total_scans: number
  type: string
  revenue: number
}

export interface ProximityRevenue {
  id: number
  name: string
  type: string
  revenue: number
  total_scans: number
  avg_dist_to_top_rides_m: number
}

export interface TransitEdge {
  from: number
  to: number
  observations: number
  p5_sec: number
  median_sec: number
  distance_m: number
  walk_sec: number
  quiet_min_sec: number | null
}

export interface TransitGraph {
  nodes: TransitNode[]
  edges: TransitEdge[]
  triangle_violations: number
  triangle_violation_samples: {
    a: number; b: number; c: number
    a_name: string; b_name: string; c_name: string
    deficit: number
  }[]
  proximity_revenue: ProximityRevenue[]
  top_rides_for_proximity: { id: number; name: string }[]
  node_count: number
  edge_count: number
}

export interface IntelligenceData {
  heatmap: {
    attractions: HeatmapAttraction[]
    hours: number[]
    max_scans: number
  }
  velocity: {
    points: VelocityPoint[]
    total: number
    peak_hour: number
    peak_revenue: number
  }
  plan_scatter: PlanScatterPoint[]
  staff_sparklines: StaffSparkline[]
  insights: Insight[]
  attraction_deep: AttractionDeep[]
  agent_deep: AgentDeep[]
  transit_graph: TransitGraph | null
}

export function useIntelligence(eventId: number | null) {
  const [data, setData] = useState<IntelligenceData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!eventId || !Number.isFinite(eventId)) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)

    fetch(`/api/dashboard/${eventId}/intelligence`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? (r.json() as Promise<IntelligenceData>) : null))
      .then((d) => {
        if (!cancelled) {
          setData(d ?? null)
          setLoading(false)
        }
      })
      .catch(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [eventId])

  return { data, loading }
}
